import {
  PartnerApiKeyMintResponse,
  PartnerBranding,
  PartnerBrandingSubdomainResponse,
  PartnerBrandingSubdomainUpdate,
  PartnerApiKeysResponse,
  PartnerCreatePayload,
  PartnerFeatureSettings,
  PartnerFeatureSettingsUpdate,
  PartnerListItem,
  PartnerOrgCreatePayload,
  PartnerPackagingSettings,
  PartnerPackagingSettingsUpdate,
  PartnerUpdatePayload,
  PartnerUser,
  PartnerUserCreatePayload,
  PartnerUserUpdatePayload,
  Partner,
  PartnerEnabledJurisdictionsResponse,
  PartnerEnabledJurisdictionsUpdate,
} from '@/types/partners';
import { PaginationApiResponse } from '@/types/shared';
import api, { V2_ADMIN_PREFIX } from './api';

// The admin partners table renders every row (no pagination controls yet), so
// request the max page size the backend allows to keep showing all partners.
// A dedicated pagination UI is the follow-up to CP-2324.
const PARTNERS_PAGE_SIZE = 100;

const getPartnerById = async (partnerId: string) => {
  return api.get<Partner>(`${V2_ADMIN_PREFIX}/partner/${partnerId}`);
};

const getPartners = async (searchQuery?: string) => {
  const { data } = await api.get<PaginationApiResponse<PartnerListItem>>(`${V2_ADMIN_PREFIX}/partners`, {
    params: {
      size: PARTNERS_PAGE_SIZE,
      ...(searchQuery ? { search_query: searchQuery } : {}),
    },
  });
  return data.items;
};

const getPartnersReport = async () => {
  return api.get(`${V2_ADMIN_PREFIX}/partner/report/download`, {
    responseType: 'blob',
    headers: { Accept: 'application/octet-stream' },
  });
};

const createPartner = async (payload: PartnerCreatePayload) => {
  const { data } = await api.post(`${V2_ADMIN_PREFIX}/partners`, payload);
  return data;
};

const updatePartner = async (partnerId: string, payload: PartnerUpdatePayload) => {
  const { data } = await api.patch<Partner>(`${V2_ADMIN_PREFIX}/partner/${partnerId}`, payload);
  return data;
};

const getPartnerUsers = async (partnerId: string) => {
  const { data } = await api.get<PartnerUser[]>(`${V2_ADMIN_PREFIX}/partner/${partnerId}/users`);
  return data;
};

const createPartnerUser = async (partnerId: string, payload: PartnerUserCreatePayload) => {
  const { data } = await api.post<PartnerUser>(`${V2_ADMIN_PREFIX}/partner/${partnerId}/users`, payload);
  return data;
};

const updatePartnerUser = async (partnerId: string, userId: string, payload: PartnerUserUpdatePayload) => {
  const { data } = await api.patch<PartnerUser>(`${V2_ADMIN_PREFIX}/partner/${partnerId}/users/${userId}`, payload);
  return data;
};

const deletePartnerUser = async (partnerId: string, userId: string) => {
  await api.delete(`${V2_ADMIN_PREFIX}/partner/${partnerId}/users/${userId}`);
};

const addPartnerOrg = async (partnerId: string, payload: PartnerOrgCreatePayload) => {
  const { data } = await api.post(`${V2_ADMIN_PREFIX}/partner/${partnerId}/organizations`, payload);
  return data;
};

const removePartnerOrg = async (partnerId: string, organizationId: string) => {
  await api.delete(`${V2_ADMIN_PREFIX}/partner/${partnerId}/organizations/${organizationId}`);
};

// Mints a portfolio-scope API key for the partner. The plaintext token is
// returned once, so callers must reveal it immediately and never persist it.
const mintPartnerPortfolioApiKey = async (partnerId: string) => {
  const { data } = await api.post<PartnerApiKeyMintResponse>(`${V2_ADMIN_PREFIX}/partner/${partnerId}/api-keys`);
  return data;
};

// Lists the partner's portfolio API keys (metadata only, never the token) so an
// admin can audit and revoke them.
const getPartnerPortfolioApiKeys = async (partnerId: string) => {
  const { data } = await api.get<PartnerApiKeysResponse>(`${V2_ADMIN_PREFIX}/partner/${partnerId}/api-keys`);
  return data;
};

const revokePartnerPortfolioApiKey = async (partnerId: string, apiKeyId: string) => {
  await api.delete(`${V2_ADMIN_PREFIX}/partner/${partnerId}/api-keys/${apiKeyId}`);
};

const getPartnerEnabledJurisdictions = async (partnerId: string) => {
  const { data } = await api.get<PartnerEnabledJurisdictionsResponse>(
    `${V2_ADMIN_PREFIX}/partner/${partnerId}/enabled-jurisdictions`
  );
  return data;
};

const updatePartnerEnabledJurisdictions = async (partnerId: string, payload: PartnerEnabledJurisdictionsUpdate) => {
  const { data } = await api.put<PartnerEnabledJurisdictionsResponse>(
    `${V2_ADMIN_PREFIX}/partner/${partnerId}/enabled-jurisdictions`,
    payload
  );
  return data;
};

const getPartnerBranding = async (partnerId: string) => {
  const { data } = await api.get<PartnerBranding>(`${V2_ADMIN_PREFIX}/partner/${partnerId}/branding`);
  return data;
};

const setPartnerBrandingSubdomain = async (partnerId: string, payload: PartnerBrandingSubdomainUpdate) => {
  const { data } = await api.put<PartnerBrandingSubdomainResponse>(
    `${V2_ADMIN_PREFIX}/partner/${partnerId}/branding/subdomain`,
    payload
  );
  return data;
};

const clearPartnerBrandingSubdomain = async (partnerId: string) => {
  const { data } = await api.delete<PartnerBrandingSubdomainResponse>(
    `${V2_ADMIN_PREFIX}/partner/${partnerId}/branding/subdomain`
  );
  return data;
};

const getPartnerSettings = async (partnerId: string) => {
  const { data } = await api.get<PartnerFeatureSettings>(`${V2_ADMIN_PREFIX}/partner/${partnerId}/settings`);
  return data;
};

const updatePartnerSettings = async (partnerId: string, payload: PartnerFeatureSettingsUpdate) => {
  const { data } = await api.put<PartnerFeatureSettings>(`${V2_ADMIN_PREFIX}/partner/${partnerId}/settings`, payload);
  return data;
};

const getPartnerPackaging = async (partnerId: string) => {
  const { data } = await api.get<PartnerPackagingSettings>(`${V2_ADMIN_PREFIX}/partner/${partnerId}/packaging`);
  return data;
};

const updatePartnerPackaging = async (partnerId: string, payload: PartnerPackagingSettingsUpdate) => {
  const { data } = await api.put<PartnerPackagingSettings>(
    `${V2_ADMIN_PREFIX}/partner/${partnerId}/packaging`,
    payload
  );
  return data;
};

const downloadBillingReport = async (startDate: string, endDate: string) => {
  const response = await api.get<Blob>(`${V2_ADMIN_PREFIX}/billing-report/download`, {
    params: { start_date: startDate, end_date: endDate },
    responseType: 'blob',
    timeout: 120_000,
  });
  return { data: response.data, headers: response.headers as Record<string, string> };
};

const PARTNERS_BY_ID_STATE_KEY = 'partners_by_id';
const PARTNERS_LIST_KEY = 'partners_list';
const PARTNER_USERS_KEY = 'partner_users';
const PARTNER_API_KEYS_KEY = 'partner_api_keys';
const PARTNER_ENABLED_JURISDICTIONS_KEY = 'partner_enabled_jurisdictions';
const PARTNER_PACKAGING_KEY = 'partner_packaging';
const PARTNER_SETTINGS_KEY = 'partner_settings';
const PARTNER_BRANDING_KEY = 'partner_branding';

export {
  getPartnerById,
  getPartners,
  getPartnersReport,
  createPartner,
  updatePartner,
  getPartnerUsers,
  createPartnerUser,
  updatePartnerUser,
  deletePartnerUser,
  addPartnerOrg,
  removePartnerOrg,
  mintPartnerPortfolioApiKey,
  getPartnerPortfolioApiKeys,
  revokePartnerPortfolioApiKey,
  getPartnerEnabledJurisdictions,
  updatePartnerEnabledJurisdictions,
  getPartnerPackaging,
  updatePartnerPackaging,
  getPartnerSettings,
  updatePartnerSettings,
  getPartnerBranding,
  setPartnerBrandingSubdomain,
  clearPartnerBrandingSubdomain,
  PARTNER_BRANDING_KEY,
  downloadBillingReport,
  PARTNERS_BY_ID_STATE_KEY,
  PARTNERS_LIST_KEY,
  PARTNER_USERS_KEY,
  PARTNER_API_KEYS_KEY,
  PARTNER_ENABLED_JURISDICTIONS_KEY,
  PARTNER_PACKAGING_KEY,
  PARTNER_SETTINGS_KEY,
};
